import "./index.css";

import { useEffect, useState, useRef } from "react";
import { Button, Input, message } from "antd";
import { MailOutlined } from "@ant-design/icons";
import { useNavigate } from "react-router-dom";

import ThemeToggle from "../../components/ThemeToggle";
import { supabase } from "../../lib/supabase";
const CAPTCHA_COOLDOWN_TIME = 30;

// Common email domains for China and US
const COMMON_EMAIL_DOMAINS = [
  "gmail.com",
  "yahoo.com",
  "outlook.com",
  "hotmail.com",
  "qq.com",
  "163.com",
  "126.com",
  "sina.com",
  "sohu.com",
  "yeah.net",
  "foxmail.com",
  "icloud.com",
  "aol.com",
  "live.com",
  "msn.com",
];

const Login = () => {
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [captcha, setCaptcha] = useState("");
  const [captchaCountdown, setCaptchaCountdown] = useState(0);
  const [getCaptchaLoading, setGetCaptchaLoading] = useState(false);
  const [loginLoading, setLoginCaptchaLoading] = useState(false);
  const [showAutocomplete, setShowAutocomplete] = useState(false);
  const [filteredDomains, setFilteredDomains] = useState([]);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const autocompleteRef = useRef(null);
  const inputRef = useRef(null);

  const handleEmailChange = (e) => {
    const value = e.target.value;
    setEmail(value);
    
    // Show autocomplete if user is typing and has @ symbol
    if (value.includes('@')) {
      const [localPart, domainPart] = value.split('@');
      if (domainPart) {
        const filtered = COMMON_EMAIL_DOMAINS.filter(domain => 
          domain.toLowerCase().startsWith(domainPart.toLowerCase())
        );
        setFilteredDomains(filtered);
        setShowAutocomplete(filtered.length > 0);
        setSelectedIndex(-1);
      } else {
        setShowAutocomplete(false);
      }
    } else {
      setShowAutocomplete(false);
    }
  };

  const handleCaptchaChange = (e) => {
    setCaptcha(e.target.value);
  };

  const handleKeyDown = (e) => {
    if (!showAutocomplete) return;

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setSelectedIndex(prev => 
          prev < filteredDomains.length - 1 ? prev + 1 : 0
        );
        break;
      case 'ArrowUp':
        e.preventDefault();
        setSelectedIndex(prev => 
          prev > 0 ? prev - 1 : filteredDomains.length - 1
        );
        break;
      case 'Enter':
        e.preventDefault();
        if (selectedIndex >= 0) {
          selectDomain(filteredDomains[selectedIndex]);
        }
        break;
      case 'Escape':
        setShowAutocomplete(false);
        setSelectedIndex(-1);
        break;
    }
  };

  const selectDomain = (domain) => {
    const [localPart] = email.split('@');
    const newEmail = `${localPart}@${domain}`;
    setEmail(newEmail);
    setShowAutocomplete(false);
    setSelectedIndex(-1);
    inputRef.current?.focus();
  };

  const handleInputFocus = () => {
    if (email.includes('@')) {
      const [localPart, domainPart] = email.split('@');
      if (domainPart) {
        const filtered = COMMON_EMAIL_DOMAINS.filter(domain => 
          domain.toLowerCase().startsWith(domainPart.toLowerCase())
        );
        setFilteredDomains(filtered);
        setShowAutocomplete(filtered.length > 0);
      }
    }
  };

  const handleInputBlur = () => {
    // Delay hiding autocomplete to allow for clicks
    setTimeout(() => {
      setShowAutocomplete(false);
      setSelectedIndex(-1);
    }, 200);
  };

  // Close autocomplete when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (autocompleteRef.current && !autocompleteRef.current.contains(event.target)) {
        setShowAutocomplete(false);
        setSelectedIndex(-1);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  async function getCaptcha() {
    if (!email || !email.includes('@')) {
      message.error("请输入有效的邮箱地址");
      return;
    }

    setGetCaptchaLoading(true);
    const { data, error } = await supabase.auth.signInWithOtp({
      email,
      options: {
        shouldCreateUser: false,
      },
    });

    if (error?.name === "AuthApiError") {
      message.error("您的邮箱不在白名单内，请检查您的邮箱或联系管理员");

      setGetCaptchaLoading(false);
      return;
    }

    message.success("验证码已发送");
    setCaptchaCountdown(CAPTCHA_COOLDOWN_TIME);
    const countdownInterval = setInterval(() => {
      setCaptchaCountdown((prevCountdown) => {
        if (prevCountdown <= 1) {
          clearInterval(countdownInterval);
          return 0;
        }

        return prevCountdown - 1;
      });
    }, 1000);

    setGetCaptchaLoading(false);
  }

  async function login() {
    if (!email || !captcha) {
      message.error("请填写邮箱和验证码");
      return;
    }

    setLoginCaptchaLoading(true);
    const { data, error } = await supabase.auth.verifyOtp({
      email,
      token: captcha,
      type: "email",
    });

    if (error) {
      message.error("登陆失败");
    } else {
      localStorage.setItem("auth", JSON.stringify(data));
      message.success("登录成功");
      navigate("/search");
    }

    setLoginCaptchaLoading(false);
  }

  const checkUserStatus = async () => {
    const {
      data: { user },
    } = await supabase.auth.getUser();

    if (user) {
      navigate("/search");
    }
  };

  useEffect(() => {
    checkUserStatus();
  }, []);

  return (
    <div className="main">
      <div className="theme-toggle-container">
        <ThemeToggle size="large" />
      </div>
      <div className="login-form">
        <div className="logo">早培校友平台</div>
        
        <div className="email-section">
          <div className="email-autocomplete" ref={autocompleteRef}>
            <Input
              ref={inputRef}
              placeholder="请输入您的邮箱地址"
              prefix={<MailOutlined />}
              value={email}
              onChange={handleEmailChange}
              onKeyDown={handleKeyDown}
              onFocus={handleInputFocus}
              onBlur={handleInputBlur}
            />
            {showAutocomplete && (
              <div className="autocomplete-dropdown">
                {filteredDomains.map((domain, index) => (
                  <div
                    key={domain}
                    className={`autocomplete-item ${index === selectedIndex ? 'selected' : ''}`}
                    onClick={() => selectDomain(domain)}
                  >
                    @{domain}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        <div className="otp-section">
          <div className="otp-container">
            <Input
              className="ant-input" // force same style as email input
              placeholder="填写验证码"
              value={captcha}
              onChange={handleCaptchaChange}
            />
            <Button
              className="otp-container-button"
              onClick={getCaptcha}
              loading={getCaptchaLoading}
              disabled={captchaCountdown > 0}
            >
              {captchaCountdown > 0
                ? `${captchaCountdown}秒后重新获取`
                : "获取验证码"}
            </Button>
          </div>
        </div>

        <Button
          className="submit-button"
          type="primary"
          onClick={login}
          loading={loginLoading}
        >
          登录
        </Button>
      </div>
    </div>
  );
}

export default Login;
